import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const TenantContext = createContext(null);

const FALLBACK = {
  id: "default",
  name: "Soubor One",
  tagline: "Where it all clicks.",
  subtitle: "Customers, apps, invoices, leads, quotes, support. All in one sleek workspace.",
  logo_mark: "S",
  logo_url: "/brand/soubor-one-mark.png",
  primary_hue: 36,
  primary_saturation: 95,
  primary_lightness: 52,
  powered_by: "SAL",
  footer_text: null,
};

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(FALLBACK);
  const [ready, setReady] = useState(false);

  const fetchTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant");
      setTenant({ ...FALLBACK, ...data });
    } catch {
      setTenant(FALLBACK);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { fetchTenant(); }, [fetchTenant]);

  // Apply tenant primary color as CSS variables
  useEffect(() => {
    const h = tenant.primary_hue ?? 36;
    const s = tenant.primary_saturation ?? 95;
    const l = tenant.primary_lightness ?? 52;
    const darkL = Math.max(l - 10, 20);
    const mutedLight = `${h} ${s}% 96%`;
    const mutedDark = `${h} ${Math.max(s - 40, 20)}% 15%`;

    const root = document.documentElement;
    // Light theme
    root.style.setProperty("--primary", `${h} ${s}% ${l}%`);
    root.style.setProperty("--primary-hover", `${h} ${s}% ${darkL}%`);
    root.style.setProperty("--primary-muted", mutedLight);

    // Dark theme overrides (applies when .dark class is on html)
    const styleId = "sal-tenant-dark-overrides";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `.dark{--primary:${h} ${Math.min(s + 5, 100)}% ${Math.min(l + 8, 72)}%;--primary-hover:${h} ${Math.min(s + 10, 100)}% ${Math.min(l + 15, 78)}%;--primary-muted:${mutedDark};}`;

    // Tab title
    document.title = tenant.name ? `${tenant.name} · Dashboard` : "Dashboard";
  }, [tenant]);

  const update = async (patch) => {
    const { data } = await api.patch("/tenant", patch);
    setTenant({ ...FALLBACK, ...data });
    return data;
  };

  return (
    <TenantContext.Provider value={{ tenant, ready, refresh: fetchTenant, update }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext) || { tenant: FALLBACK };
